import type { BroadcastCheckResultDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { broadcastErrorText, readBroadcastError } from '../errors';
import { useSetBroadcastChat } from '../mutations';
import { BroadcastCheckResult } from './broadcast-check-result';

interface Props {
  cityId: string;
  /** Текст ошибки снаружи (422 chat_migrated из «Проверить связь»/тумблера). Читается только на маунте. */
  initialError?: string | null;
  onBound: (check: BroadcastCheckResultDto) => void;
}

/**
 * PUT /chat. 422 — ошибка ввода: под полем (с check — список прав вместо склеенного message);
 * 409/503/прочее — тост.
 */
export function BroadcastChatForm({ cityId, initialError = null, onBound }: Props) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(initialError);
  const [failedCheck, setFailedCheck] = useState<BroadcastCheckResultDto | null>(null);
  const setChat = useSetBroadcastChat(cityId);
  const trimmed = value.trim();
  const inputId = `broadcast-chat-${cityId}`;
  const errorId = `${inputId}-error`;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!trimmed) return;
    setError(null);
    setFailedCheck(null);
    try {
      const check = await setChat.mutateAsync(trimmed);
      setValue('');
      onBound(check);
    } catch (err) {
      const apiErr = await readBroadcastError(err);
      if (apiErr?.status === 422) {
        if (apiErr.check) setFailedCheck(apiErr.check);
        else setError(apiErr.message);
        return;
      }
      toast.error(broadcastErrorText(apiErr, 'Не удалось привязать чат'));
    }
  };

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="space-y-2">
      <Label htmlFor={inputId}>Чат: id, @username или ссылка t.me/…</Label>
      <div className="flex gap-2">
        <Input
          id={inputId}
          value={value}
          maxLength={200}
          placeholder="-1001234567890 или @city_chat"
          onChange={(e) => setValue(e.target.value)}
          aria-invalid={error !== null}
          {...(error !== null ? { 'aria-describedby': errorId } : {})}
          className="max-w-md"
        />
        <Button type="submit" disabled={!trimmed || setChat.isPending}>
          Привязать
        </Button>
      </div>
      {error !== null && (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {failedCheck && <BroadcastCheckResult check={failedCheck} />}
    </form>
  );
}
